import { Spinner } from "blocks/components/Spinner";
import { usePageContext } from "lib/context/page-context";
import { Fragment, useMemo } from "react";
import { trpc } from "utils/trpc";
import { getKanbanData, reconcileKanbanState } from "../transforms";
import { TableBackedKanban } from "./TableBackedKanban";
import { sortBy } from "lodash";
import { ZeroState } from "./zero-states";
import { KanbanHeader } from "./KanbanHeader";
import { throwIfString } from "lib/utils/throwIfString";
import type { KanbanBlock as KanbanBlockType } from "../../types";
import type { BlockProps } from "blocks/v2/__shared__/types";

export const ConnectedEditorKanban = (props: BlockProps<KanbanBlockType>) => {
  const { tableId } = props.block.config;

  const { accountId } = usePageContext();

  const { data: fieldsResponse } = trpc.tableFields.list.useQuery(
    {
      tableId: tableId as string,
    },
    {
      enabled: !!tableId && !!accountId,
      suspense: false,
    }
  );

  const fields = useMemo(() => {
    if (fieldsResponse)
      return sortBy(fieldsResponse.data, (field) => field.options?.order);
  }, [fieldsResponse]);

  const { data: recordsResponse } = trpc.blockKanban.queryRecords.useQuery(
    {
      tableId: tableId as string,
      filter: { limit: 1000 },
      blockId: props.block.config.id as string,
    },
    {
      enabled: !!tableId && !!accountId && !!props.block.config.id,
      suspense: false,
    }
  );

  const records = useMemo(() => {
    return recordsResponse?.data;
  }, [recordsResponse?.data]);

  const kanbanState = useMemo(() => {
    if (!props.block.config.selectedColumns || !records) return undefined;

    return reconcileKanbanState(props.block.config.selectedColumns, records)[0];
  }, [props.block.config.selectedColumns, records]);

  if (
    !props.block.config.tableId ||
    !props.block.config.groupFieldId ||
    !props.block.config.selectedColumns?.length ||
    !kanbanState
  ) {
    return <ZeroState title="Configure your component" />;
  }

  if (!fields || !records) return <Spinner height={200} />;

  const kanbanData = getKanbanData({
    fields,
    records,
    selectedColumns: props.block.config.selectedColumns ?? [],
    groupFieldId: throwIfString(props.block.config.groupFieldId),
    titleFieldId: throwIfString(props.block.config.cardTitleFieldId),
    descriptionFieldId: throwIfString(
      props.block.config.cardDescriptionFieldId
    ),
    kanbanState,
  });

  return (
    <Fragment>
      <KanbanHeader
        title={props.block.config.title}
        newCardButtonCopy={props.block.config.newCardButtonCopy}
        onClickCreate={props.block.config.canCreateCards ? noOp : undefined}
      />
      <TableBackedKanban
        data={kanbanData}
        accountId={accountId}
        tableId={props.block.config.tableId}
        disableScroll
      />
    </Fragment>
  );
};

function noOp() {
  throw new Error("Not implemented");
}
